import React, { useState, useMemo, useRef } from "react";
import { Box, Typography, Snackbar, Alert } from "@mui/material";

// ==========================================
// UTILITY FUNCTIONS
// ==========================================
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function randBetween(min, max) {
  return Math.random() * (max - min) + min;
}

// ==========================================
// SUB-COMPONENTS
// ==========================================
// Change #100249 - "text is being truncated on the wheel". Every label was
// drawn at one fixed size along a line only (rOuter - rInner) long, so a name
// over ~11 characters ("Golden Ticket £100") ran off the end and was cut.
// The size now shrinks to fit the label in its line, down to a readable floor.
const wedgeLabelFontSize = (label, lineLength) => {
  const chars = Math.max(1, String(label).length);
  // ~0.62 of the font size per character at weight 900, plus letter spacing.
  const fit = lineLength / (chars * 0.62 + chars * 0.07);
  return Math.max(1.5, Math.min(3.1, fit)).toFixed(2);
};

function WedgeLabels({ segments, anglePer }) {
  const cx = 50;
  const cy = 50;
  const rOuter = 46;
  const rInner = 22;

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        <defs>
          {segments.map((seg, i) => {
            const centerAngle = i * anglePer + anglePer / 2;
            const pOuter = polarToCartesian(cx, cy, rOuter, centerAngle);
            const pInner = polarToCartesian(cx, cy, rInner, centerAngle);
            const pathId = `segPath_${i}`;
            const d = `M ${pOuter.x} ${pOuter.y} L ${pInner.x} ${pInner.y}`;
            return <path key={pathId} id={pathId} d={d} fill="none" />;
          })}
        </defs>
        {segments.map((seg, i) => {
          const pathId = `segPath_${i}`;
          return (
            <text
              key={`text_${i}`}
              fill={seg.SpinnerTextColour || "#ffffff"}
              fontWeight="900"
              fontSize={wedgeLabelFontSize(seg.RewardName || "Prize", rOuter - rInner)}
              letterSpacing="0.2"
              style={{ textShadow: "0 2px 0 rgba(0,0,0,0.25)" }}
            >
              <textPath
                href={`#${pathId}`}
                startOffset="50%"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {seg.RewardName || "Prize"}
              </textPath>
            </text>
          );
        })}
      </svg>
    </Box>
  );
}

function buildSeparatorsOverlay(count) {
  const c = Math.max(2, count);
  const anglePer = 360 / c;
  const lines = [];
  for (let i = 0; i < c; i++) {
    const a = i * anglePer;
    lines.push(`rgba(255,255,255,0) ${a}deg ${a + anglePer - 1}deg`);
    lines.push(
      `rgba(255,255,255,0.9) ${a + anglePer - 1}deg ${a + anglePer}deg`
    );
  }
  return `conic-gradient(${lines.join(",")})`;
}

// ==========================================
// MAIN PREVIEW COMPONENT
// ==========================================
export default function LoyaltySpinnerPreview({ config }) {
  const [rotationDeg, setRotationDeg] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [transitionMs, setTransitionMs] = useState(0);
  const wheelRef = useRef(null);
  const [snack, setSnack] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  const segments = useMemo(() => {
    const basePrizes = config.prizes || [];
    if (basePrizes.length === 0) return [];

    const expanded = [];
    let maxCount = Math.max(
      ...basePrizes.map((p) => parseInt(p.QtySpinnerSegment) || 1),
      1
    );

    for (let i = 0; i < maxCount; i++) {
      basePrizes.forEach((p) => {
        if (i < (parseInt(p.QtySpinnerSegment) || 1)) {
          expanded.push(p);
        }
      });
    }
    return expanded;
  }, [config.prizes]);

  const anglePer = segments.length > 0 ? 360 / segments.length : 360;

  const conicBg = useMemo(() => {
    if (!segments.length) return "#ccc";
    const stops = segments.map(
      (s, i) =>
        `${s.SpinnerBackgroundColour || "#ccc"} ${i * anglePer}deg ${
          (i + 1) * anglePer
        }deg`
    );
    return `conic-gradient(${stops.join(",")})`;
  }, [segments, anglePer]);

  const handleSpin = () => {
    if (isSpinning || segments.length < 2) return;
    setIsSpinning(true);

    const winnerIndex = Math.floor(Math.random() * segments.length);
    const durMs = (config.SpinDuration || 5) * 1000;
    const spins = config.SpinCount || 3;

    const center = winnerIndex * anglePer + anglePer / 2;
    const targetWithin360 = (360 - center) % 360;
    const current = ((rotationDeg % 360) + 360) % 360;
    const delta = (targetWithin360 - current + 360) % 360;
    const nextRotation = rotationDeg + spins * 360 + delta;

    setTransitionMs(durMs);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setRotationDeg(nextRotation))
    );

    setTimeout(() => {
      setIsSpinning(false);
      setSnack({
        open: true,
        text: `Landed on: ${segments[winnerIndex].RewardName}`,
        severity: "success",
      });
    }, durMs + 50);
  };

  const wheelSize = { xs: 320, sm: 420, md: 500 };

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `10px solid ${config.SpinnerHeadBackgroundColour || "#a1185a"}`,
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        position: "relative",
        overflow: "hidden",
        background: config.WidgetOuterColour || "#f5f5f5",
        p: { xs: 1.5, sm: 2.5 },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", pt: 1 }}>
        <Typography
          sx={{
            fontSize: { xs: 40, sm: 56 },
            fontWeight: 900,
            letterSpacing: 2,
            color: config.SpinnerHeadBackgroundColour || "#a1185a",
            textShadow: "0 3px 0 rgba(0,0,0,0.12)",
            lineHeight: 1,
          }}
        >
          {config.Name}
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            mt: 1,
            px: 2,
            py: 0.6,
            borderRadius: 999,
            background: config.SpinnerHeadBackgroundColour || "#a1185a",
            color: "white",
            fontWeight: 800,
            letterSpacing: 0.5,
            fontSize: 12,
          }}
        >
          {config.Subtitle}
        </Box>
      </Box>

      {/* Wheel area */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          placeItems: "center",
          position: "relative",
          pb: 2,
        }}
      >
        {/* Pointer */}
        <Box
          sx={{
            position: "absolute",
            top: 6,
            zIndex: 5,
            width: 0,
            height: 0,
            borderLeft: "18px solid transparent",
            borderRight: "18px solid transparent",
            borderBottom: "28px solid #f3d37a",
            filter: "drop-shadow(0 3px 2px rgba(0,0,0,0.35))",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 38,
            zIndex: 5,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "#f3d37a",
            boxShadow: "0 3px 8px rgba(0,0,0,0.35)",
          }}
        />

        {/* Wheel container */}
        <Box
          onClick={handleSpin}
          sx={{
            width: wheelSize,
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            position: "relative",
            cursor: isSpinning ? "not-allowed" : "pointer",
            userSelect: "none",
          }}
        >
          {/* Outer ring */}
          <Box
            sx={{
              position: "absolute",
              inset: -10,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(0,0,0,0.0), rgba(0,0,0,0.25))",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: config.SpinnerOuterWheelColour || "#0d2230",
              boxShadow: "inset 0 0 0 10px rgba(255,255,255,0.2)",
            }}
          />

          {/* Spinning disc */}
          <Box
            ref={wheelRef}
            sx={{
              position: "absolute",
              inset: 18,
              borderRadius: "50%",
              background: conicBg,
              transform: `rotate(${rotationDeg}deg)`,
              transition: transitionMs
                ? `transform ${transitionMs}ms cubic-bezier(0.12, 0.85, 0.12, 1)`
                : "none",
              boxShadow: "inset 0 0 0 6px rgba(255,255,255,0.25)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: buildSeparatorsOverlay(segments.length),
                opacity: 0.35,
                pointerEvents: "none",
              }}
            />

            <WedgeLabels segments={segments} anglePer={anglePer} />

            {/* Center cap */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%,-50%)",
                width: "18%",
                height: "18%",
                borderRadius: "50%",
                background: config.SpinnerCentreBackgroundColour || "#f3d37a",
                boxShadow: "0 6px 12px rgba(0,0,0,0.35)",
                display: "grid",
                placeItems: "center",
                color: config.SpinnerCentreTextColour || "#464645",
                fontWeight: 900,
                letterSpacing: 0.5,
              }}
            >
              <Typography
                sx={{ fontWeight: 900, fontSize: { xs: 10, sm: 12 } }}
              >
                SPIN
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ borderRadius: 2 }}
        >
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
